import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface CalculationResult {
  regularCost: number;
  shopGlobalCost: number;
  savings: number;
  savingsPercentage: number;
  taxSavings: number;
}

export default function SavingsCalculator() {
  const navigate = useNavigate();
  const [weight, setWeight] = useState(2.5);
  const [length, setLength] = useState(12);
  const [width, setWidth] = useState(8);
  const [height, setHeight] = useState(4);
  const [declaredValue, setDeclaredValue] = useState(100);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<CalculationResult | null>(null);

  const calculateSavings = async () => {
    setCalculating(true);

    // Simulate API call to backend calculator
    setTimeout(() => {
      // Sample calculation logic
      const volume = length * width * height;
      const baseShipping = 35 + weight * 8 + volume * 0.01; // Typical international shipping
      const taxSavings = declaredValue * 0.07; // 7% Delaware sales tax savings
      const consolidatedCost = baseShipping * 0.25; // 75% savings through consolidation
      const serviceFee = Math.min(baseShipping * 0.1, 12); // Max $12 service fee
      const totalShopGlobal = consolidatedCost + serviceFee + 5; // +$5 customs processing

      const savings = baseShipping - totalShopGlobal + taxSavings;
      const savingsPercentage = Math.round((savings / baseShipping) * 100);

      setResult({
        regularCost: Math.round(baseShipping),
        shopGlobalCost: Math.round(totalShopGlobal),
        savings: Math.round(savings),
        savingsPercentage,
        taxSavings: Math.round(taxSavings),
      });
      setCalculating(false);
    }, 1000);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <h3 className="text-xl font-bold text-center mb-6">
        Calculate Your Savings
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Package Weight (lbs)
          </label>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="2.5"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Length (in)
            </label>
            <input
              type="number"
              min="1"
              value={length}
              onChange={(e) => setLength(parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Width (in)
            </label>
            <input
              type="number"
              min="1"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Height (in)
            </label>
            <input
              type="number"
              min="1"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Declared Value (CAD)
          </label>
          <input
            type="number"
            min="1"
            value={declaredValue}
            onChange={(e) => setDeclaredValue(parseInt(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="100"
          />
        </div>

        <button
          onClick={calculateSavings}
          disabled={calculating}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {calculating ? "Calculating..." : "Calculate Savings"}
        </button>
      </div>

      {result && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-3">
            🎉 Your Savings Results
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Regular Shipping:</span>
              <span className="line-through text-gray-500">
                ${result.regularCost}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">ShopGlobal Pro:</span>
              <span className="font-semibold text-green-700">
                ${result.shopGlobalCost}
              </span>
            </div>
            <div className="border-t border-green-300 pt-2">
              <div className="flex justify-between font-semibold text-lg">
                <span className="text-green-800">You Save:</span>
                <span className="text-green-600">
                  ${result.savings} ({result.savingsPercentage}%)
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                Includes ${result.taxSavings} tax savings from Delaware address
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-center">
        <button
          onClick={() => navigate("/signup")}
          className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700">
          Get Started - Save ${result ? result.savings : "Money"}
        </button>
      </div>
    </div>
  );
}